import React, { useState, useEffect, useCallback, useRef } from 'react';

function getCsrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') : '';
}

const STEP_PHONE = 'phone';
const STEP_CODE = 'code';

function WhatsappOtpLogin({ requestUrl, verifyUrl, loginUrl, translations }) {
  const [step, setStep] = useState(STEP_PHONE);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const phoneInputRef = useRef(null);
  const codeBoxRefs = useRef([]);

  const headers = {
    'Content-Type': 'application/json',
    'X-CSRF-Token': getCsrfToken(),
    Accept: 'application/json'
  };

  const focusBox = useCallback((i) => {
    const clamped = Math.max(0, Math.min(5, i));
    const el = codeBoxRefs.current[clamped];
    if (el) el.focus();
  }, []);

  useEffect(() => {
    if (step === STEP_PHONE && phoneInputRef.current) phoneInputRef.current.focus();
    if (step === STEP_CODE) focusBox(0);
  }, [step, focusBox]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;

    const interval = setInterval(() => {
      setResendIn((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [resendIn]);

  const handlePhoneChange = useCallback((e) => {
    setPhoneNumber(e.target.value);
    setError(null);
  }, []);

  const handleBoxChange = useCallback(
    (i, e) => {
      const digits = e.target.value.replace(/\D/g, '');
      const arr = code.padEnd(6, ' ').slice(0, 6).split('').map((c) => (c === ' ' ? '' : c));

      if (digits.length === 0) {
        arr[i] = '';
        setCode(arr.join('').slice(0, 6));
        setError(null);
        return;
      }

      const targetIndex = code[i] ? i : Math.min(i, code.length);
      arr[targetIndex] = digits[0];
      setCode(arr.join('').slice(0, 6));
      setError(null);
      focusBox(targetIndex + 1);
    },
    [code, focusBox]
  );

  const handleBoxKeyDown = useCallback(
    (i, e) => {
      if (e.key === 'Backspace') {
        const arr = code.padEnd(6, ' ').slice(0, 6).split('').map((c) => (c === ' ' ? '' : c));

        if (!code[i]) {
          e.preventDefault();
          arr[i - 1] = '';
          setCode(arr.join('').slice(0, 6));
          setError(null);
          focusBox(i - 1);
        } else {
          e.preventDefault();
          arr[i] = '';
          setCode(arr.join('').slice(0, 6));
          setError(null);
        }
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        focusBox(i - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        focusBox(i + 1);
      }
    },
    [code, focusBox]
  );

  const handleBoxPaste = useCallback(
    (i, e) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
      if (pasted.length === 0) return;

      const arr = code.padEnd(6, ' ').slice(0, 6).split('').map((c) => (c === ' ' ? '' : c));
      let pos = Math.min(i, code.length);
      for (let k = 0; k < pasted.length && pos < 6; k += 1, pos += 1) {
        arr[pos] = pasted[k];
      }

      setCode(arr.join('').slice(0, 6));
      setError(null);
      focusBox(pos - 1);
    },
    [code, focusBox]
  );

  const sendCode = useCallback(
    (isResend) => {
      const trimmedPhone = phoneNumber.trim();
      if (!trimmedPhone) {
        setError(translations.missing_phone);
        return;
      }

      if (isResend) {
        setResending(true);
      } else {
        setSubmitting(true);
      }
      setError(null);

      fetch(requestUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ phone_number: trimmedPhone })
      })
        .then((res) => res.json().catch(() => null))
        .then((data) => {
          setSubmitting(false);
          setResending(false);

          if (!data) {
            setError(translations.send_failed);
            return;
          }

          if (data.success) {
            setPhoneNumber(trimmedPhone);
            setMaskedPhone(data.masked_phone || maskedPhone);
            setResendIn(data.resend_in || 0);
            setCode('');
            setStep(STEP_CODE);
          } else {
            setError(data.errors || translations.send_failed);
          }
        })
        .catch(() => {
          setSubmitting(false);
          setResending(false);
          setError(translations.send_failed);
        });
    },
    [phoneNumber, requestUrl, maskedPhone, translations]
  );

  const handlePhoneSubmit = useCallback(
    (e) => {
      if (e) e.preventDefault();
      if (submitting) return;
      sendCode(false);
    },
    [submitting, sendCode]
  );

  const handleResend = useCallback(() => {
    if (resending || resendIn > 0) return;
    sendCode(true);
  }, [resending, resendIn, sendCode]);

  const verifyCode = useCallback(() => {
    if (code.length !== 6 || submitting) return;

    setSubmitting(true);
    setError(null);

    fetch(verifyUrl, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({ phone_number: phoneNumber, code: code })
    })
      .then((res) => {
        if (res.status === 401) {
          window.location.href = loginUrl;
          return null;
        }
        return res.json().catch(() => null);
      })
      .then((data) => {
        if (!data) return;

        if (data.success) {
          const redirectUrl = data.redirect_url || '/';

          if (window.Turbolinks) {
            Turbolinks.visit(redirectUrl);
          } else {
            window.location.href = redirectUrl;
          }
        } else {
          setError(data.errors || translations.invalid_code);
          setCode('');
          setSubmitting(false);

          focusBox(0);
        }
      })
      .catch(() => {
        setError(translations.invalid_code);
        setCode('');
        setSubmitting(false);

        focusBox(0);
      });
  }, [code, submitting, verifyUrl, phoneNumber, loginUrl, translations, focusBox]);

  const handleCodeSubmit = useCallback(
    (e) => {
      if (e) e.preventDefault();
      verifyCode();
    },
    [verifyCode]
  );

  useEffect(() => {
    if (step === STEP_CODE && code.length === 6) {
      verifyCode();
    }
  }, [step, code, verifyCode]);

  const handleChangeNumber = useCallback(() => {
    setStep(STEP_PHONE);
    setCode('');
    setError(null);
    setResendIn(0);
  }, []);

  const resendLabel =
    resendIn > 0
      ? translations.resend_in.replace('__seconds__', String(resendIn))
      : translations.resend;

  const codeStepDescription = (translations.code_step_description || '').replace(
    '__phone__',
    maskedPhone
  );

  return (
    <div className="two-factor-challenge whatsapp-login">
      <a href={loginUrl} className="two-factor-challenge__back login-back-link">
        <i className="zmdi zmdi-arrow-left" />
        {translations.back_to_login}
      </a>

      <div className="two-factor-challenge__hero">
        <div className="two-factor-challenge__shield whatsapp-login__glyph">
          <i className="zmdi zmdi-whatsapp" />
        </div>

        <div className="two-factor-challenge__hero-copy">
          <h2 className="two-factor-challenge__title">{translations.title}</h2>
          <p className="two-factor-challenge__desc">
            {step === STEP_PHONE ? translations.phone_step_description : codeStepDescription}
          </p>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}

      {step === STEP_PHONE && (
        <form onSubmit={handlePhoneSubmit}>
          <label htmlFor="whatsapp-login-phone" className="two-factor-challenge__label">
            {translations.phone_label}
          </label>

          <div className="form-group whatsapp-login__phone-group">
            <input
              ref={phoneInputRef}
              id="whatsapp-login-phone"
              type="tel"
              className="form-control"
              value={phoneNumber}
              onChange={handlePhoneChange}
              placeholder={translations.phone_placeholder}
              autoComplete="tel"
              inputMode="tel"
              disabled={submitting}
            />
            <i className="form-group__bar" />
          </div>

          <button
            type="submit"
            className="btn btn-primary btn--raised login-action login-action--primary mt-4"
            disabled={submitting || !phoneNumber.trim()}
          >
            {submitting ? translations.sending : translations.send_button}
            {!submitting && <span className="login-btn__arrow">&rarr;</span>}
          </button>
        </form>
      )}

      {step === STEP_CODE && (
        <form onSubmit={handleCodeSubmit}>
          <label htmlFor="whatsapp-login-code-0" className="two-factor-challenge__label">
            {translations.code_label}
          </label>

          <div className="whatsapp-login__otp">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <input
                key={i}
                id={i === 0 ? 'whatsapp-login-code-0' : undefined}
                ref={(el) => {
                  codeBoxRefs.current[i] = el;
                }}
                type="text"
                className="whatsapp-login__otp-box"
                value={code[i] || ''}
                onChange={(e) => handleBoxChange(i, e)}
                onKeyDown={(e) => handleBoxKeyDown(i, e)}
                onPaste={(e) => handleBoxPaste(i, e)}
                maxLength={1}
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete={i === 0 ? 'one-time-code' : 'off'}
                aria-label={`${translations.code_label} ${i + 1}`}
                disabled={submitting}
              />
            ))}
          </div>

          <button
            type="submit"
            className="btn btn-primary btn--raised login-action login-action--primary mt-4"
            disabled={submitting || code.length !== 6}
          >
            {submitting ? translations.verifying : translations.verify_button}
            {!submitting && <span className="login-btn__arrow">&rarr;</span>}
          </button>

          <div className="whatsapp-login__resend-row">
            <button
              type="button"
              className="whatsapp-login__resend"
              onClick={handleResend}
              disabled={resending || resendIn > 0}
            >
              <i className="zmdi zmdi-refresh" />
              {resending ? translations.sending : resendLabel}
            </button>

            <button
              type="button"
              className="whatsapp-login__change"
              onClick={handleChangeNumber}
            >
              {translations.change_number}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default WhatsappOtpLogin;
